import { useReducer } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import type {
  AssetKind,
  IncomeFrequency,
  LiabilityKind,
  ManualEntryKind,
  ManualEntryRequest,
  ManualFinancialEntry,
} from "../../../../lib/onboarding-api";

// ─── Form state ───────────────────────────────────────────────────────────────

interface FormState {
  displayName: string;
  // Liability
  liabilityKind: LiabilityKind | "";
  monthlyPaymentDollars: string;
  // Income
  incomeFrequency: IncomeFrequency;
  incomeAmountDollars: string;
  // Asset
  assetKind: AssetKind | "";
  assetValueDollars: string;
}

const emptyFormState: FormState = {
  displayName: "",
  liabilityKind: "",
  monthlyPaymentDollars: "",
  incomeFrequency: "monthly",
  incomeAmountDollars: "",
  assetKind: "",
  assetValueDollars: "",
};

type ReducerAction =
  | { type: "reset"; state: FormState }
  | { type: "set"; field: keyof FormState; value: string };

function formReducer(state: FormState, action: ReducerAction): FormState {
  if (action.type === "reset") return action.state;
  return { ...state, [action.field]: action.value };
}

function dollarsToCents(dollars: string): number | undefined {
  const parsed = parseFloat(dollars.replace(/,/g, ""));
  if (isNaN(parsed) || parsed < 0) return undefined;
  return Math.round(parsed * 100);
}

function centsToDollars(cents: number | undefined): string {
  if (cents == null) return "";
  return (cents / 100).toFixed(2);
}

function formStateFromEntry(entry: ManualFinancialEntry): FormState {
  return {
    displayName: entry.display_name,
    liabilityKind: (entry.liability_kind as LiabilityKind) ?? "",
    monthlyPaymentDollars: centsToDollars(entry.monthly_payment_cents),
    incomeFrequency: entry.income_frequency ?? "monthly",
    incomeAmountDollars: centsToDollars(entry.income_amount_cents),
    assetKind: (entry.asset_kind as AssetKind) ?? "",
    assetValueDollars: centsToDollars(entry.asset_value_cents),
  };
}

function formToRequest(
  form: FormState,
  kind: ManualEntryKind,
): ManualEntryRequest | null {
  const displayName = form.displayName.trim();
  if (!displayName) return null;

  if (kind === "liability") {
    const monthly = dollarsToCents(form.monthlyPaymentDollars);
    if (monthly == null) return null;
    return {
      entry_kind: "liability",
      display_name: displayName,
      liability_kind: form.liabilityKind || undefined,
      monthly_payment_cents: monthly,
    };
  }

  if (kind === "income") {
    const amount = dollarsToCents(form.incomeAmountDollars);
    if (amount == null) return null;
    return {
      entry_kind: "income",
      display_name: displayName,
      income_frequency: form.incomeFrequency,
      income_amount_cents: amount,
    };
  }

  // asset
  const value = dollarsToCents(form.assetValueDollars);
  if (value == null) return null;
  return {
    entry_kind: "asset",
    display_name: displayName,
    asset_kind: form.assetKind || undefined,
    asset_value_cents: value,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface ManualEntryFormProps {
  kind: ManualEntryKind;
  initial?: ManualFinancialEntry;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (req: ManualEntryRequest) => void;
  onCancel: () => void;
}

export function ManualEntryForm({
  kind,
  initial,
  isSubmitting,
  error,
  onSubmit,
  onCancel,
}: ManualEntryFormProps) {
  const [form, dispatch] = useReducer(
    formReducer,
    initial ? formStateFromEntry(initial) : emptyFormState,
  );
  const set = (field: keyof FormState) => (value: string) =>
    dispatch({ type: "set", field, value });

  const handleSubmit = () => {
    const req = formToRequest(form, kind);
    if (!req) return;
    onSubmit(req);
  };

  const isEditing = Boolean(initial);
  const namePlaceholder =
    kind === "liability"
      ? "e.g. Student loan"
      : kind === "income"
        ? "e.g. Rental income"
        : "e.g. Chase savings";

  return (
    <div
      data-slot="manual-entry-form"
      className="space-y-4 border-t border-border-soft pt-4"
    >
      <h4 className="text-sm font-semibold text-foreground">
        {isEditing ? "Edit entry" : "Add entry"}
      </h4>

      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">
          Name
        </label>
        <Input
          placeholder={namePlaceholder}
          value={form.displayName}
          onChange={(e) => set("displayName")(e.target.value)}
        />
      </div>

      {kind === "liability" && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Monthly payment ($)
            </label>
            <Input
              type="number"
              min={0}
              placeholder="e.g. 450"
              value={form.monthlyPaymentDollars}
              onChange={(e) => set("monthlyPaymentDollars")(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Debt type (optional)
            </label>
            <select
              className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm"
              value={form.liabilityKind}
              onChange={(e) => set("liabilityKind")(e.target.value)}
            >
              <option value="">Select…</option>
              <option value="auto">Auto</option>
              <option value="student">Student</option>
              <option value="personal">Personal</option>
              <option value="mortgage">Mortgage</option>
              <option value="heloc">HELOC</option>
              <option value="bnpl">Buy now pay later</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      )}

      {kind === "income" && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Amount ($)
            </label>
            <Input
              type="number"
              min={0}
              placeholder="e.g. 2500"
              value={form.incomeAmountDollars}
              onChange={(e) => set("incomeAmountDollars")(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Frequency
            </label>
            <select
              className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm"
              value={form.incomeFrequency}
              onChange={(e) => set("incomeFrequency")(e.target.value)}
            >
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
        </div>
      )}

      {kind === "asset" && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Current value ($)
            </label>
            <Input
              type="number"
              min={0}
              placeholder="e.g. 15000"
              value={form.assetValueDollars}
              onChange={(e) => set("assetValueDollars")(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">
              Account type (optional)
            </label>
            <select
              className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm"
              value={form.assetKind}
              onChange={(e) => set("assetKind")(e.target.value)}
            >
              <option value="">Select…</option>
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
              <option value="investment">Investment</option>
              <option value="retirement">Retirement (401k/IRA)</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={isSubmitting}
          onClick={handleSubmit}
          className="gap-1.5"
        >
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEditing ? "Save changes" : "Add entry"}
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
